import { useEffect, useState, useCallback } from "react";
import { Link } from "wouter";
import { NurseLayout } from "@/components/layout/nurse-layout";
import { AdvicelineNurseLayout } from "@/components/layout/adviceline-nurse-layout";
import {
  Inbox,
  Pill,
  Droplets,
  Search,
  Filter,
  Clock,
  Mail,
  Building2,
  User,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  CheckCircle2,
  Send,
  Truck,
  Package,
  Eye,
  Stethoscope,
  Loader2,
  X,
  Phone,
  Plus,
  Globe,
  BarChart3,
  TrendingUp,
  MapPin,
  Syringe,
} from "lucide-react";
import { useListIncomingPatientRequests } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { SameTeamBadge } from "@/components/ui/same-team-badge";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type FilterType = "" | "prescription-query" | "blood-form-request";

// ── Prescription tracking status constants ──────────────────────────────
const PRESCRIPTION_STATUS_OPTIONS = [
  { value: "acknowledged", label: "Acknowledged", icon: Eye, color: "bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-200" },
  { value: "no_recent_bloods", label: "No recent bloods", icon: Stethoscope, color: "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200" },
  { value: "sent_to_pharmacy", label: "Sent to pharmacy", icon: Send, color: "bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-200" },
  { value: "contact_supplier", label: "Contact supplier", icon: Truck, color: "bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200" },
  { value: "ready_for_collection", label: "Ready for collection", icon: Package, color: "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200" },
  { value: "custom", label: "Custom…", icon: CheckCircle2, color: "bg-violet-100 text-violet-700 border-violet-200 hover:bg-violet-200" },
] as const;

const BLOOD_FORM_STATUS_OPTIONS = [
  { value: "sent_via_post", label: "Sent via post", icon: Send, color: "bg-teal-100 text-teal-700 border-teal-200 hover:bg-teal-200" },
] as const;

const PRESCRIPTION_STATUS_LABELS: Record<string, string> = {
  acknowledged: "Acknowledged",
  no_recent_bloods: "No recent bloods",
  sent_to_pharmacy: "Sent to pharmacy",
  contact_supplier: "Contact supplier",
  ready_for_collection: "Ready for collection",
  custom: "Custom",
};

const BLOOD_FORM_STATUS_LABELS: Record<string, string> = {
  sent_via_post: "Sent via post",
};

// ── Helpers ────────────────────────────────────────────────────────────

function RelativeTime({ iso }: { iso: string }) {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let label: string;
  if (diffMins < 2) label = "just now";
  else if (diffMins < 60) label = `${diffMins} mins ago`;
  else if (diffHours < 24) label = `${diffHours}h ago`;
  else if (diffDays === 1) label = "yesterday";
  else if (diffDays < 7) label = `${diffDays} days ago`;
  else label = date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  return (
    <time dateTime={iso} title={date.toLocaleString("en-GB", { timeZone: "Europe/London" })}>
      {label}
    </time>
  );
}

// ── Tracking status update section ─────────────────────────────────────

function TrackingSection({
  request,
  onUpdated,
}: {
  request: {
    id: number;
    requestType: "prescription-query" | "blood-form-request";
    prescriptionStatus?: string | null;
    bloodFormStatus?: string | null;
    trackingNote?: string | null;
  };
  onUpdated: () => void;
}) {
  const isPrescription = request.requestType === "prescription-query";
  const [updating, setUpdating] = useState<string | null>(null);
  const [customNote, setCustomNote] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentStatus = isPrescription
    ? request.prescriptionStatus
    : request.bloodFormStatus;

  const statusLabel = currentStatus
    ? isPrescription
      ? (PRESCRIPTION_STATUS_LABELS[currentStatus] ?? currentStatus)
      : (BLOOD_FORM_STATUS_LABELS[currentStatus] ?? currentStatus)
    : null;

  const options = isPrescription
    ? PRESCRIPTION_STATUS_OPTIONS
    : BLOOD_FORM_STATUS_OPTIONS;

  const handleUpdate = useCallback(async (value: string) => {
    setUpdating(value);
    setError(null);

    // If "custom" is selected, show input first
    if (value === "custom") {
      setShowCustomInput(true);
      setUpdating(null);
      return;
    }

    try {
      const res = await fetch(`${BASE}/api/nurse/patient-requests/${request.id}/tracking`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          requestType: request.requestType,
          ...(isPrescription ? { prescriptionStatus: value } : { bloodFormStatus: value }),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Update failed" }));
        setError(err.error ?? "Update failed");
        return;
      }

      onUpdated();
    } catch {
      setError("Network error — please try again");
    } finally {
      setUpdating(null);
    }
  }, [request.id, request.requestType, isPrescription, onUpdated]);

  const handleCustomSubmit = useCallback(async () => {
    if (!customNote.trim()) return;
    setUpdating("custom-saving");
    setError(null);

    try {
      const res = await fetch(`${BASE}/api/nurse/patient-requests/${request.id}/tracking`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          requestType: request.requestType,
          prescriptionStatus: "custom",
          trackingNote: customNote.trim(),
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Update failed" }));
        setError(err.error ?? "Update failed");
        return;
      }

      setCustomNote("");
      setShowCustomInput(false);
      onUpdated();
    } catch {
      setError("Network error — please try again");
    } finally {
      setUpdating(null);
    }
  }, [request.id, request.requestType, customNote, onUpdated]);

  return (
    <div className="border-t border-slate-100 pt-4">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
        Tracking status
      </p>

      {statusLabel && (
        <div className="mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {statusLabel}
          {request.trackingNote && (
            <span className="text-emerald-500 ml-1">— {request.trackingNote}</span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const Icon = opt.icon;
          const isActive = currentStatus === opt.value && !showCustomInput;
          const isLoading = updating === opt.value || updating === "custom-saving";

          return (
            <div key={opt.value}>
              {opt.value === "custom" && showCustomInput ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customNote}
                    onChange={(e) => setCustomNote(e.target.value)}
                    placeholder="Describe the status…"
                    maxLength={500}
                    className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 w-48"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCustomSubmit();
                      if (e.key === "Escape") { setShowCustomInput(false); setCustomNote(""); }
                    }}
                  />
                  <button
                    onClick={handleCustomSubmit}
                    disabled={!customNote.trim() || isLoading}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 transition-colors"
                  >
                    {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
                  </button>
                  <button
                    onClick={() => { setShowCustomInput(false); setCustomNote(""); }}
                    className="p-1.5 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleUpdate(opt.value)}
                  disabled={isLoading || (isActive && !showCustomInput)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-50 ${
                    isActive
                      ? "bg-emerald-100 text-emerald-700 border-emerald-300 ring-1 ring-emerald-300"
                      : opt.color
                  }`}
                >
                  {isLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Icon className="h-3.5 w-3.5" />
                  )}
                  {opt.label}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-600 flex items-center gap-1">
          <X className="h-3 w-3" /> {error}
        </p>
      )}
    </div>
  );
}

// ── Request row ────────────────────────────────────────────────────────

function RequestRow({
  request,
  viewerTeamCode,
  onTrackingUpdated,
}: {
  request: {
    id: number;
    requestType: "prescription-query" | "blood-form-request";
    patientName: string;
    patientDob?: string | null;
    nhsNumber?: string | null;
    hospitalName?: string | null;
    medicationName?: string | null;
    queryDetails?: string | null;
    testType?: string | null;
    testNotes?: string | null;
    medicationForm?: string | null;
    bloodTestDone?: string | null;
    bloodTestDate?: string | null;
    bloodTestLocation?: string | null;
    formattedBody: string;
    adviceLineEmail?: string | null;
    createdAt: string;
    assignedToUserId?: string | null;
    assignedToName?: string | null;
    assignedToTeamCode?: string | null;
    status?: string | null;
    prescriptionStatus?: string | null;
    bloodFormStatus?: string | null;
    trackingNote?: string | null;
    sourceChannel?: string | null;
    callerCli?: string | null;
  };
  viewerTeamCode: string | null;
  onTrackingUpdated: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const isPrescription = request.requestType === "prescription-query";

  const trackingStatusLabel = isPrescription
    ? (request.prescriptionStatus ? PRESCRIPTION_STATUS_LABELS[request.prescriptionStatus] ?? request.prescriptionStatus : null)
    : (request.bloodFormStatus ? BLOOD_FORM_STATUS_LABELS[request.bloodFormStatus] ?? request.bloodFormStatus : null);

  return (
    <div className={`bg-white border rounded-2xl overflow-hidden transition-shadow hover:shadow-md ${expanded ? "shadow-md" : "shadow-sm"} ${isPrescription ? "border-violet-100" : "border-teal-100"}`}>
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full text-left px-5 py-4 flex items-center gap-4"
      >
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${isPrescription ? "bg-violet-50" : "bg-teal-50"}`}>
          {isPrescription
            ? <Pill className="h-5 w-5 text-violet-600" />
            : <Droplets className="h-5 w-5 text-teal-600" />
          }
        </div>

        <div className="flex-1 min-w-0 grid sm:grid-cols-3 gap-1 sm:gap-4 items-start">
          <div className="sm:col-span-1">
            <p className="font-semibold text-slate-900 text-sm truncate">
              {isPrescription ? "Prescription Query" : "Blood Form Request"}
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <User className="h-3 w-3 text-slate-400 shrink-0" />
              <p className="text-xs text-slate-600 truncate">{request.patientName}</p>
              {request.sourceChannel === "phone" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 text-sky-700 px-2 py-0.5 text-[10px] font-medium shrink-0">
                  <Phone className="h-3 w-3" /> Phone caller
                </span>
              )}
            </div>
          </div>

          <div className="sm:col-span-1 hidden sm:block">
            <p className="text-xs text-slate-500 truncate">
              {isPrescription
                ? (request.medicationName ?? "—")
                : (request.testType ?? "—")}
            </p>
            {trackingStatusLabel && (
              <div className="flex items-center gap-1 mt-0.5">
                <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
                <p className="text-xs text-emerald-600 truncate">{trackingStatusLabel}</p>
              </div>
            )}
            {request.hospitalName && (
              <div className="flex items-center gap-1 mt-0.5">
                <Building2 className="h-3 w-3 text-slate-300 shrink-0" />
                <p className="text-xs text-slate-400 truncate">{request.hospitalName}</p>
              </div>
            )}
          </div>

          <div className="sm:col-span-1 flex sm:justify-end items-start gap-3">
            <div className="text-right">
              <div className="flex items-center gap-1 text-xs text-slate-400 justify-end">
                <Clock className="h-3 w-3 shrink-0" />
                <RelativeTime iso={request.createdAt} />
              </div>
              {request.adviceLineEmail ? (
                <p className="text-xs text-teal-600 mt-0.5 truncate max-w-[180px]">
                  ✉ {request.adviceLineEmail}
                </p>
              ) : (
                <p className="text-xs text-amber-500 mt-0.5">No email sent</p>
              )}
            </div>
            {expanded
              ? <ChevronUp className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
              : <ChevronDown className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
            }
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Patient details</p>
              <div className="space-y-1">
                <p className="text-slate-700"><span className="text-slate-400 text-xs">Name:</span> {request.patientName}</p>
                {request.sourceChannel === "phone" && request.callerCli && <p className="text-slate-700"><span className="text-slate-400 text-xs">Caller number:</span> {request.callerCli}</p>}
                {request.patientDob && <p className="text-slate-700"><span className="text-slate-400 text-xs">DOB:</span> {request.patientDob}</p>}
                {request.nhsNumber && <p className="text-slate-700"><span className="text-slate-400 text-xs">NHS no:</span> {request.nhsNumber}</p>}
                {request.hospitalName && <p className="text-slate-700"><span className="text-slate-400 text-xs">Hospital:</span> {request.hospitalName}</p>}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Request details</p>
              <div className="space-y-1">
                {isPrescription ? (
                  <>
                    {request.medicationForm && (
                      <p className="text-slate-700">
                        <span className="text-slate-400 text-xs">Type of medicine:</span>{" "}
                        {request.medicationForm === "tablet" ? "Tablets"
                          : request.medicationForm === "injection" ? "Injection"
                          : request.medicationForm === "both" ? "Both tablets and injection"
                          : "Patient not sure"}
                      </p>
                    )}
                    {request.bloodTestDone && (
                      <p className="text-slate-700">
                        <span className="text-slate-400 text-xs">Recent blood test:</span>{" "}
                        {request.bloodTestDone === "yes" ? "Yes"
                          : request.bloodTestDone === "no" ? "No"
                          : "Not sure"}
                        {request.bloodTestDate ? ` · ${request.bloodTestDate}` : ""}
                        {request.bloodTestLocation ? ` · at ${request.bloodTestLocation}` : ""}
                      </p>
                    )}
                    {request.medicationName && <p className="text-slate-700"><span className="text-slate-400 text-xs">Patient-named drug:</span> {request.medicationName}</p>}
                    {request.queryDetails && <p className="text-slate-700 leading-relaxed"><span className="text-slate-400 text-xs">Other notes:</span> {request.queryDetails}</p>}
                  </>
                ) : (
                  <>
                    {request.testType && <p className="text-slate-700"><span className="text-slate-400 text-xs">Test:</span> {request.testType}</p>}
                    {request.testNotes && <p className="text-slate-700 leading-relaxed"><span className="text-slate-400 text-xs">Notes:</span> {request.testNotes}</p>}
                  </>
                )}
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Formatted message sent to advice line</p>
            <pre className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-xl p-4 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto">
              {request.formattedBody}
            </pre>
          </div>

          {request.adviceLineEmail && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Mail className="h-3.5 w-3.5 text-teal-500 shrink-0" />
              <span>Email forwarded to <span className="font-medium text-slate-700">{request.adviceLineEmail}</span></span>
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-slate-400">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            <span>Submitted {new Date(request.createdAt).toLocaleString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })} (UK time)</span>
          </div>

          {/* Medication Reference — prescription queries only */}
          {isPrescription && (
            <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide flex items-center gap-1.5">
                <Pill className="h-3.5 w-3.5" />
                Medication Reference
              </p>

              <div className="grid sm:grid-cols-2 gap-3">
                {/* Medication name & form */}
                <div className="space-y-2">
                  {request.medicationName && (
                    <div className="flex items-start gap-2">
                      <Pill className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-slate-400">Medication</p>
                        <p className="text-sm font-medium text-slate-800">{request.medicationName}</p>
                      </div>
                    </div>
                  )}
                  {request.medicationForm && (
                    <div className="flex items-start gap-2">
                      {request.medicationForm === "injection" ? (
                        <Syringe className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                      ) : request.medicationForm === "tablet" ? (
                        <Pill className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                      ) : request.medicationForm === "both" ? (
                        <Syringe className="h-4 w-4 text-violet-500 mt-0.5 shrink-0" />
                      ) : (
                        <Pill className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
                      )}
                      <div>
                        <p className="text-xs text-slate-400">Form</p>
                        <p className="text-sm font-medium text-slate-800">
                          {request.medicationForm === "tablet" ? "Tablets"
                            : request.medicationForm === "injection" ? "Injection"
                            : request.medicationForm === "both" ? "Both tablets and injection"
                            : "Not specified"}
                        </p>
                      </div>
                    </div>
                  )}
                  {!request.medicationName && !request.medicationForm && (
                    <p className="text-xs text-slate-400 italic">No medication details provided</p>
                  )}
                </div>

                {/* Blood test status */}
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Stethoscope className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-slate-400">Blood test</p>
                      {request.bloodTestDone ? (
                        <p className="text-sm font-medium">
                          {request.bloodTestDone === "yes" ? (
                            <span className="text-emerald-700">Done</span>
                          ) : request.bloodTestDone === "no" ? (
                            <span className="text-red-600">Not done</span>
                          ) : (
                            <span className="text-amber-600">Unsure</span>
                          )}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-400 italic">Not reported</p>
                      )}
                      {(request.bloodTestDate || request.bloodTestLocation) && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-xs text-slate-500">
                          {request.bloodTestDate && (
                            <span className="flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" />
                              {request.bloodTestDate}
                            </span>
                          )}
                          {request.bloodTestLocation && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {request.bloodTestLocation}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Query details / patient notes */}
              {request.queryDetails && (
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-xs text-slate-400 mb-1">Patient notes</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{request.queryDetails}</p>
                </div>
              )}
            </div>
          )}

          {request.assignedToUserId && (
            <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
              <User className="h-3.5 w-3.5 shrink-0" />
              <span>
                Assigned to{" "}
                <span className="text-slate-700">
                  {request.assignedToName ?? request.assignedToUserId}
                </span>
                {request.assignedToTeamCode ? ` (Team ${request.assignedToTeamCode})` : ""}
              </span>
              <SameTeamBadge
                viewerTeamCode={viewerTeamCode}
                otherTeamCode={request.assignedToTeamCode}
              />
            </div>
          )}

          {/* Tracking status section */}
          <TrackingSection request={request} onUpdated={onTrackingUpdated} />

          {/* Assignment and resolve actions */}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${BASE}/api/nurse/patient-requests/${request.id}/assign`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ assignToUserId: null }) });
                  if (res.ok) onTrackingUpdated();
                } catch {}
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100 transition-colors"
            >
              <User className="h-3.5 w-3.5" />
              Assign to me
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await fetch(`${BASE}/api/nurse/patient-requests/${request.id}/resolve`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" } });
                  if (res.ok) { onTrackingUpdated(); (window as any).umami?.track?.("request-resolved"); }
                } catch {}
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Resolve &amp; sign-off
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Channel Analytics ──────────────────────────────────────────────────

type RequestWithChannel = {
  sourceChannel?: string | null;
  specialty?: string | null;
  createdAt: string;
};

function ChannelAnalytics({ requests }: { requests: RequestWithChannel[] }) {
  // Channel breakdown
  const webCount = requests.filter(r => r.sourceChannel === "web").length;
  const phoneCount = requests.filter(r => r.sourceChannel === "phone").length;
  const emailCount = requests.filter(r => r.sourceChannel === "email").length;
  const total = requests.length;

  const pct = (n: number) => total > 0 ? Math.round((n / total) * 100) : 0;

  // Specialty breakdown
  const specialtyMap = new Map<string, number>();
  requests.forEach(r => {
    const s = r.specialty || "unknown";
    specialtyMap.set(s, (specialtyMap.get(s) || 0) + 1);
  });
  const specialties = [...specialtyMap.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  // Trend: last 7 days vs previous 7 days
  const now = Date.now();
  const daysAgo = (d: number) => now - d * 86400000;
  const last7 = requests.filter(r => new Date(r.createdAt).getTime() >= daysAgo(7)).length;
  const prev7 = requests.filter(r => {
    const t = new Date(r.createdAt).getTime();
    return t >= daysAgo(14) && t < daysAgo(7);
  }).length;
  const trend7 = prev7 > 0 ? Math.round(((last7 - prev7) / prev7) * 100) : last7 > 0 ? 100 : 0;

  return (
    <div className="mb-5 space-y-4">
      {/* Channel breakdown */}
      <div>
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
          <BarChart3 className="h-3.5 w-3.5" />
          Channel Breakdown
        </p>
        <div className="flex flex-wrap gap-3">
          <div className="bg-sky-50 border border-sky-200 rounded-xl px-4 py-2.5 text-center min-w-24">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Globe className="h-3.5 w-3.5 text-sky-500" />
              <p className="text-xl font-bold text-sky-700">{webCount}</p>
            </div>
            <p className="text-xs text-sky-500 font-medium">Web · {pct(webCount)}%</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-center min-w-24">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Phone className="h-3.5 w-3.5 text-amber-500" />
              <p className="text-xl font-bold text-amber-700">{phoneCount}</p>
            </div>
            <p className="text-xs text-amber-500 font-medium">Phone · {pct(phoneCount)}%</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 text-center min-w-24">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Mail className="h-3.5 w-3.5 text-emerald-500" />
              <p className="text-xl font-bold text-emerald-700">{emailCount}</p>
            </div>
            <p className="text-xs text-emerald-500 font-medium">Email · {pct(emailCount)}%</p>
          </div>
        </div>
      </div>

      {/* Trend indicator */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span className="flex items-center gap-1">
          <TrendingUp className={`h-3.5 w-3.5 ${trend7 >= 0 ? "text-emerald-500" : "text-red-500"}`} />
          <span>Last 7 days: <strong className="text-slate-700">{last7}</strong></span>
          {prev7 > 0 && (
            <span className={trend7 >= 0 ? "text-emerald-600" : "text-red-500"}>
              ({trend7 >= 0 ? "+" : ""}{trend7}% vs prev week)
            </span>
          )}
        </span>
      </div>

      {/* Specialty breakdown */}
      {specialties.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">By Specialty</p>
          <div className="flex flex-wrap gap-2">
            {specialties.map(([specialty, count]) => (
              <span
                key={specialty}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200"
              >
                <span className="capitalize">{specialty}</span>
                <span className="text-slate-400">{count}</span>
              </span>
            ))}
            {specialtyMap.size > 6 && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs text-slate-400">
                +{specialtyMap.size - 6} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────

export default function NurseIncomingRequests() {
  const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState<FilterType>("");
    const [specialtyFilter, setSpecialtyFilter] = useState<string>("all");
    const [viewerTeamCode, setViewerTeamCode] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    fetch(`${BASE}/api/user/profile-summary`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((me) => setViewerTeamCode((me as { teamCode?: string | null } | null)?.teamCode ?? null))
      .catch(() => {});

    // Fetch team data to auto-set specialty filter
    fetch(`${BASE}/api/teams/mine`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((teams: any[]) => {
        const specialties = [...new Set(teams.map((t: any) => t.specialty).filter(Boolean))];
        if (specialties.length === 1) {
          setSpecialtyFilter(specialties[0] as string);
        }
      })
      .catch(() => {});
  }, []);

  const params = {
    ...(typeFilter ? { requestType: typeFilter as "prescription-query" | "blood-form-request" } : {}),
    ...(specialtyFilter !== "all" ? { specialty: specialtyFilter } : {}),
    limit: 100,
  };  

  const { data: requests, isLoading, error, refetch } = useListIncomingPatientRequests(params);

  const handleTrackingUpdated = useCallback(() => {
    refetch();
    setRefreshKey(k => k + 1);
  }, [refetch]);

  const filtered = (requests ?? []).filter(r => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.patientName.toLowerCase().includes(q) ||
      (r.medicationName ?? "").toLowerCase().includes(q) ||
      (r.testType ?? "").toLowerCase().includes(q) ||
      (r.hospitalName ?? "").toLowerCase().includes(q) ||
      (r.callerCli ?? "").includes(q) ||
      (r.nhsNumber ?? "").includes(q)
    );
  });

  const prescriptionCount = (requests ?? []).filter(r => r.requestType === "prescription-query").length;
  const bloodCount = (requests ?? []).filter(r => r.requestType === "blood-form-request").length;

  const isAdvicelineHub = typeof window !== "undefined" && window.location.hostname.includes("advicelinehub");
  const Layout = isAdvicelineHub ? AdvicelineNurseLayout : NurseLayout;

  return (
    <Layout title="Incoming Requests">
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <div className="bg-teal-100 p-2.5 rounded-xl">
            <Inbox className="h-6 w-6 text-teal-700" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Incoming Requests</h2>
        </div>
        <div className="flex items-center gap-2">
          <Link href="/nurse/log-call" className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors no-underline">
            <Phone className="h-3.5 w-3.5" />
            Log a call
          </Link>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 transition-colors px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
      </div>
      <p className="text-slate-600 max-w-2xl">
        Patient prescription queries and blood form requests submitted via the {isAdvicelineHub ? "Advice Line Hub" : "RheumHub"} patient portal.
        Use the tracking buttons below each request to update its progress status.
      </p>
    </div>

      {requests && requests.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-2.5 text-center min-w-24">
            <p className="text-xl font-bold text-violet-700">{prescriptionCount}</p>
            <p className="text-xs text-violet-500 font-medium">Prescription</p>
          </div>
          <div className="bg-teal-50 border border-teal-200 rounded-xl px-4 py-2.5 text-center min-w-24">
            <p className="text-xl font-bold text-teal-700">{bloodCount}</p>
            <p className="text-xs text-teal-500 font-medium">Blood forms</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-center min-w-24">
            <p className="text-xl font-bold text-slate-700">{requests.length}</p>
            <p className="text-xs text-slate-500 font-medium">Total</p>
          </div>
        </div>
      )}

      {requests && requests.length > 0 && (
        <ChannelAnalytics requests={requests as RequestWithChannel[]} />
      )}

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by patient name, medication, test, hospital…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400 shrink-0" />
          <select
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value as FilterType)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-slate-700"
          >
            <option value="">All types</option>
            <option value="prescription-query">Prescription queries</option>
            <option value="blood-form-request">Blood form requests</option>
          </select>
          <select
            value={specialtyFilter}
            onChange={e => setSpecialtyFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 text-slate-700"
          >
            <option value="all">All specialties</option>
            <option value="rheumatology">Rheumatology</option>
            <option value="dermatology">Dermatology</option>
            <option value="oncology">Oncology</option>
            <option value="endocrinology">Endocrinology</option>
            <option value="gastroenterology">Gastroenterology</option>
            <option value="neurology">Neurology</option>
            <option value="cardiology">Cardiology</option>
            <option value="respiratory">Respiratory</option>
            <option value="nephrology">Nephrology</option>
            <option value="urology">Urology</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <LoadingScreen />
      ) : error ? (
        <div className="text-center py-16 text-slate-500">
          <Inbox className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Unable to load requests</p>
          <p className="text-sm mt-1">Please try refreshing.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Inbox className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">
            {(requests ?? []).length === 0 ? "No patient requests yet" : "No requests match your search"}
          </p>
          <p className="text-sm mt-1 text-slate-400">
            {(requests ?? []).length === 0
              ? "Patient requests submitted via the portal will appear here."
              : "Try adjusting your search or filter."}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-4">
            {filtered.length} request{filtered.length !== 1 ? "s" : ""}
            {search || typeFilter ? " (filtered)" : ""}
          </p>
          <div className="space-y-3">
            {filtered.map(r => (
              <RequestRow key={r.id} request={r} viewerTeamCode={viewerTeamCode} onTrackingUpdated={handleTrackingUpdated} />
            ))}
          </div>
        </>
      )}

      <div className="mt-6 bg-orange-50 border border-orange-200 rounded-2xl px-5 py-4 text-sm text-orange-900 space-y-2">
        <p className="font-semibold flex items-center gap-2">
          <span className="inline-block h-2 w-2 rounded-full bg-orange-500 shrink-0" />
          Safeguarding reminder
        </p>
        <p className="leading-relaxed text-orange-800">
          If you identify any safeguarding concern while reviewing patient requests — including risk of self-harm, domestic abuse, child protection issues, or any immediate risk to life — you are required to follow your organisation's safeguarding procedures without delay.
        </p>
        <ul className="text-orange-800 list-disc list-inside space-y-1 ml-1">
          <li>Contact your designated safeguarding lead immediately.</li>
          <li>If there is immediate risk to life, call <strong>999</strong>.</li>
          <li>Document the concern and actions taken in line with your local policy.</li>
        </ul>
        <p className="text-orange-700 text-xs pt-1">
          Relevant frameworks:{" "}
          <a href="https://www.nhsengland.nhs.uk/patient-safety/safeguarding/" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-900">NHSE Safeguarding Adults Framework</a>
          {" · "}
          <a href="https://www.nice.org.uk/guidance/ng76" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-900">NICE NG76 (Child Abuse &amp; Neglect)</a>
          {" · "}
          <a href="https://www.nmc.org.uk/standards/code/" target="_blank" rel="noopener noreferrer" className="underline hover:text-orange-900">NMC Code Section 16 (Raising Concerns)</a>
        </p>
      </div>

      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 text-sm text-amber-800">
        <p className="font-semibold mb-1">Advice line email backup</p>
        <p className="leading-relaxed text-amber-700">
          All requests with a matched hospital email are automatically forwarded at submission. This view is a web backup only — responses should still be made by phone or via your usual clinical systems. Patient data here is subject to your 90-day retention policy.
        </p>
      </div>
    </Layout>
  );
}
